import React from 'react';
import { useState, useMemo } from 'react';
import styles from '../css/activitiesRptUpdated.module.css';
import ReportsFooter from './ReportsFooter';
import UpdatedReportsHero from './UpdatedReportsHero';

function OmittedTicketsRptUpdated({ reportData, fromDate, toDate }) {
  // const statuses = ['In', 'Out', 'Manual'];

  const Columns = [
    { key: 'Ticket', label: 'Ticket' },
    { key: 'ScannedDateTime', label: 'Scanned Date' },
  ];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortKey, setSortKey] = useState('ticket');
  const [sortDir, setSortDir] = useState('asc');
  const [exportPulse, setExportPulse] = useState(false);

  //This is a React snippet that computes a sorted version of reportData efficiently using useMemo
  // useMemo caches the result of a computation.
  // It only recomputes sorted when sortKey or sortDir changes.
  // This avoids re-sorting on every render (which can be expensive).
  // So conceptually: “Only re-sort the data when the sort column or direction changes.”

  const sorted = useMemo(() => {
    return [...reportData].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });

      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [reportData, sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const pageData = sorted.slice((page - 1) * pageSize, page * pageSize);

  function handleSort(key) {
    // console.log('sortKey:', sortKey);
    // console.log('sample value:', sorted[0]?.[sortKey]);
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  }

  function handleExport() {
    setExportPulse(true); //Turns on animation on the Export button
    const header = Columns.map((c) => c.label).join(','); //Take each column label and joins them by comma
    //Outer map. Each r is one row that ticket and car information. Inner map: each column value Columns.map(c => `"${r[c.key]}"`).
    //pulls the value us c.key and wraps it in quotes to protect the commas inside value.
    // Good Practice b/c it makes CSV safer. join(",") joins columns in one row
    // join("/n") joins all rows into csv body such as "ticket",123 and ticket 345 on a new line
    const rows = reportData
      .map((r) =>
        Columns.map((c) => {
          let value = r[c.key];

          // Handle null
          if (value === null) return '""';

          // If it's already a date object
          if (value instanceof Date) {
            return `${value.toLocaleString()}`;
          }

          // If it's a number (real number not a string)
          if (typeof value === 'number') {
            return `${value}`;
          }

          // If it's a date-like string (ISO or parseable)
          if (typeof value === 'string') {
            const parsed = new Date(value);
            if (!isNaN(parsed) && value.includes('T')) {
              // likely ISO string
              value = parsed.toLocaleString();
            }
          }

          return `"${value ?? ''}"`;
        }).join(','),
      )
      .join('\n');
    // Create a new blob.
    // Creates a file object in memory. Combines headers + rows into one csv string and marks as text/csv.
    // It is like a virtual csv file before saving
    const blob = new Blob([header + '\n' + rows], { type: 'text/csv' });
    //This creates an invisible <a> tag.
    const a = document.createElement('a');
    //creates a temporary local URL like: http://localhost/abc123
    a.href = URL.createObjectURL(blob);
    //download this file instead of opening it
    a.download = 'export.csv';
    //This simulates a user clicking the link, starting the download
    a.click();
    //Stops the animation
    setTimeout(() => setExportPulse(false), 800);
  }

  // This is a small React component that renders a sorting
  // indicator icon next to a table column header
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span style={{ opacity: 0.25, fontSize: 10, marginLeft: 4 }}>⇅</span>;
    return <span style={{ fontSize: 10, marginLeft: 4, color: '#4f46e5' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className={styles.topDiv}>
      <UpdatedReportsHero
        reportData={reportData}
        exportPulse={exportPulse}
        sortKey={sortKey}
        sorted={sorted}
        columns={Columns}
        handleExport={() => {
          handleExport();
        }}
      />
      <div className={styles.scrollWrap}>
        {/* Table */}
        <table>
          <caption style={{ textAlign: 'left', padding: 10, fontSize: 14, color: '#555' }}>
            Omitted Tickets Report - Total Records: {reportData.length} - From {fromDate} To {toDate}
          </caption>
          <thead>
            <tr>
              {Columns.map((col) => (
                <th key={col.key} style={{ width: col.width }} onClick={() => handleSort(col.key)}>
                  {col.label}
                  <SortIcon col={col.key} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.map((row, i) => (
              <tr key={row.id} style={{ background: i % 2 === 0 ? 'white' : '#fafbff' }}>
                {/* <td>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#9ca3af' }}>
                    #{row.id}
                  </span>
                </td> */}
                <td style={{ fontWeight: 500, color: '#1e1b4b' }}>{row.Ticket}</td>
                <td style={{ color: '#6366f1', fontSize: 12 }}>{row.ScannedDateTime}</td>
                <td>{row.LaneNum}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* ── Pagination Footer ── */}
        <ReportsFooter
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalPages={totalPages}
          sorted={sorted}
        />
      </div>
    </div>
  );
}
export default OmittedTicketsRptUpdated;
